import { FC, useMemo, useState, useEffect } from "react";
import useHomeHeroQuery from "@/custom_hooks/useHomeHeroQuery";
import Container from "@/components/Container";
import { Button } from "@/components/UI/Buttons/Button";
import HealthProblemModal from "./HealthProblemModal";
import HealthProblemsCarousel from "./HealthProblemsCarousel";
import { rawFeatures } from "./constants";

import imgNuovargis from "@/assets/images/healthProblems/NHE_letinis_nuovargis.jpg";
import imgJega from "@/assets/images/healthProblems/NHE_raumenu_jega.jpg";
import imgVo2max from "@/assets/images/healthProblems/NHE_istvermes_rezervas.jpg";
import imgAutoimunines from "@/assets/images/healthProblems/NHE_autoimunines_ligos.jpg";
import imgLetines from "@/assets/images/healthProblems/NHE_letines_ligos.jpg";
import imgElgesys from "@/assets/images/healthProblems/NHE_elgesys_ir_priklausomybes.jpg";
import imgVaistai from "@/assets/images/healthProblems/NHE_vaistu_pritaikomumas.jpg";
import imgGyvensena from "@/assets/images/healthProblems/NHE_gyvensenos_pokyciai.jpg";
import imgKunas from "@/assets/images/healthProblems/NHE_kuno_sudetis.jpg";
import imgMikroelementai from "@/assets/images/healthProblems/NHE_mikroelementu_ir_vitaminu_pasisavinimas.jpg";
import imgStresas from "@/assets/images/healthProblems/NHE_stresas.jpg";
import imgPaveldimumas from "@/assets/images/healthProblems/NHE_paveldimu_ligu_rizika.jpg";
import imgCholesterolis from "@/assets/images/healthProblems/NHE_aukstas_cholesterolis.jpg";
import imgMiegas from "@/assets/images/healthProblems/NHE_miegas.jpg";
import imgMityba from "@/assets/images/healthProblems/NHE_mityba.jpg";

const images = [
  imgNuovargis,
  imgJega,
  imgVo2max,
  imgAutoimunines,
  imgLetines,
  imgElgesys,
  imgVaistai,
  imgGyvensena,
  imgKunas,
  imgMikroelementai,
  imgStresas,
  imgPaveldimumas,
  imgCholesterolis,
  imgMiegas,
  imgMityba,
];

const VISIBLE_COUNT_MOBILE = 5;
const VISIBLE_COUNT_DESKTOP = 12;

const baseFeatures = rawFeatures.map((feature, index) => ({
  ...feature,
  image: images[index % images.length],
}));

const STRATEGY_FEATURE_ORDER = [
  "Lėtinis nuovargis",
  "Stresas",
  "Miegas",
  "Paveldimų ligų rizika",
] as const;

const orderFeaturesForHero = <T extends { title: string }>(
  features: T[],
  heroQuery: string,
): T[] => {
  if (heroQuery !== "strategija") return features;

  const byTitle = new Map(features.map((feature) => [feature.title, feature]));
  const prioritized = STRATEGY_FEATURE_ORDER.flatMap((title) => {
    const feature = byTitle.get(title);
    return feature ? [feature] : [];
  });
  const prioritizedTitles = new Set<string>(STRATEGY_FEATURE_ORDER);
  const rest = features.filter((feature) => !prioritizedTitles.has(feature.title));

  return [...prioritized, ...rest];
};

const HealthProblems: FC = () => {
  const heroQuery = useHomeHeroQuery();
  const features = useMemo(
    () => orderFeaturesForHero(baseFeatures, heroQuery),
    [heroQuery],
  );

  const [expanded, setExpanded] = useState(false);
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState<
    number | null
  >(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const visibleCount = isMobile ? VISIBLE_COUNT_MOBILE : VISIBLE_COUNT_DESKTOP;

  const visibleFeatures = expanded ? features : features.slice(0, visibleCount);

  return (
    <>
      <section className="md:py-section flex flex-col items-center self-stretch bg-gray-100 px-4 py-15 md:px-6">
        <Container>
          <div className="flex flex-col items-center gap-10">
            <HealthProblemsCarousel images={images} />

            <div className="flex flex-1 flex-col items-center justify-center gap-5">
              <h2 className="text-ink text-center text-[26px] leading-tight font-light md:text-[34px]">
                Padedame spręsti svarbiausius sveikatos iššūkius
              </h2>
              <p className="text-muted text-center text-base leading-relaxed md:max-w-[580px]">
                Telomera padeda anksčiau suprasti rizikas, stiprinti silpnąsias
                vietas ir kryptingai gerinti savijautą, funkciją bei ilgalaikę
                sveikatą.
              </p>
            </div>

            <div className="relative w-full">
              <div className="grid w-full grid-cols-1 gap-2.5 md:grid-cols-2">
                {visibleFeatures.map((feature, index) => (
                  <button
                    key={feature.title}
                    onClick={() => setSelectedFeatureIndex(index)}
                    className="focus:ring-ink/20 flex items-center gap-5 rounded-[6px] border border-[#ddd] bg-white p-4 text-left transition-all hover:scale-[1.02] hover:border-gray-300 hover:bg-gray-100 hover:shadow-sm focus:ring-2 focus:outline-none"
                  >
                    <img
                      src={feature.image}
                      alt={feature.title}
                      loading="lazy"
                      className="h-20 w-20 shrink-0 rounded-[4px] object-cover"
                    />
                    <div className="flex flex-col gap-1.5">
                      <span className="text-ink text-lg leading-tight font-normal">
                        {feature.title}
                      </span>
                      <span className="text-muted text-base leading-relaxed">
                        {feature.description}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {!expanded && (
                <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-[120px] bg-gradient-to-t from-gray-100 to-transparent" />
              )}
            </div>

            <Button
              variant="white"
              size="sm"
              className="w-auto min-w-[188px] border-[#ededed] px-[18px] py-[14px] text-base"
              onClick={() => setExpanded((prev) => !prev)}
              onHover
              hoverDark
            >
              {expanded ? "Rodyti mažiau" : "Žiūrėti daugiau"}
            </Button>
          </div>
        </Container>
      </section>

      <HealthProblemModal
        isOpen={selectedFeatureIndex !== null}
        onClose={() => setSelectedFeatureIndex(null)}
        features={expanded ? features : visibleFeatures}
        selectedIndex={selectedFeatureIndex ?? 0}
        onSelectIndex={setSelectedFeatureIndex}
      />
    </>
  );
};

export default HealthProblems;
